import { Field, Formik, Form } from "formik";
import { useSelector } from "react-redux";
import React, { use, useState, useEffect } from "react";
import * as Yup from "yup";

const TypeOfChange = ({ setTypeOfChange, typeOfChange }) => {
  const [showError, setShowError] = useState(false);
  const company = useSelector(
    (state) =>
      state.companySlice?.company || state.companyDashboardSlice?.company
  );
  const { initiatedTransactions } = useSelector(
    (state) => state.initiatedTransactionsSlice
  );
  let entityType = company?.type;
  const nominalSharesLength = company?.nominalShares?.length || 0;
  // Static data to simulate Unalloted shares (temporary)
  const staticUnalloted = [{ id: "u1" }]; // Simulate having unalloted shares
  // const staticUnalloted = []; // Uncomment this to simulate no unalloted shares
  const hasUnallotedShares =
    (company?.Unalloted?.length || staticUnalloted.length) > 0;

  // Check for pending Share Capital transactions
  const hasPendingShareCapitalTransaction = initiatedTransactions?.items?.some(
    (transaction) => transaction.applicationType === "Share Capital"
  );

  // Validation schema using Yup
  const validationSchema = Yup.object().shape({
    shareCapitalSelection: Yup.string().required("Required"),
    typeOfChange: Yup.string().when([], {
      is: () => !hasPendingShareCapitalTransaction,
      then: Yup.string().required("Required"),
      otherwise: Yup.string(),
    }),
  });

  const selectionTypes = [
    {
      value: "allotmentOfShares",
      label: "Allotment of shares",
      disabled: false,
    },
  ];

  const changeTypes = [
    {
      value: "",
      label: "Select an Option",
      disabled: true,
      default: true,
    },
    {
      value: "unallotedShares",
      label: "Company with unissued shares/Existing authorizations",
      disabled: false,
    },
    {
      value: "privateWithOneClass",
      label: "Increase of Share Capital",
      disabled: false,
    },
    {
      value: "privateWithMoreThanOneClass",
      label: "Authorization with Share Distribution",
      disabled: false,
    },
    {
      value: "authorization",
      label: "Authorization",
      disabled: false,
    },
  ];

  // Function to filter changeTypes based on entityType and scenarios
  const getFilteredChangeTypes = () => {
    let allowedTypes = [""]; // Always include the "Select an Option" default

    if (entityType === "PVT") {
      if (nominalSharesLength === 1) {
        if (hasUnallotedShares) {
          // Scenario 2: One class of shares and has unalloted shares
          allowedTypes = ["", "unallotedShares", "privateWithOneClass"];
        } else {
          // Scenario 3: One class of shares and no unalloted shares
          allowedTypes = ["", "privateWithOneClass"];
        }
      } else if (nominalSharesLength > 1) {
        if (hasUnallotedShares) {
          // Scenario 2: More than one class of shares and has unalloted shares
          allowedTypes = [
            "",
            "unallotedShares",
            "privateWithMoreThanOneClass",
            "authorization",
          ];
        } else {
          // Scenario 3: More than one class of shares and no unalloted shares
          allowedTypes = ["", "privateWithMoreThanOneClass", "authorization"];
        }
      } else {
        // Scenario 1: Default case for PVT (render all)
        allowedTypes = [
          "",
          "unallotedShares",
          "privateWithOneClass",
          "privateWithMoreThanOneClass",
          "authorization",
        ];
      }
    } else if (entityType === "PLC") {
      if (hasUnallotedShares) {
        // Scenario 1 & Scenario 2: Has unalloted shares (one class or more)
        allowedTypes = [
          "",
          "unallotedShares",
          "privateWithMoreThanOneClass",
          "authorization",
        ];
      } else {
        // Scenario 2: No unalloted shares (one class or more)
        allowedTypes = ["", "privateWithMoreThanOneClass", "authorization"];
      }
    } else {
      // Default case if entityType is neither PVT nor PLC (render all to be safe)
      allowedTypes = [
        "",
        "unallotedShares",
        "privateWithOneClass",
        "privateWithMoreThanOneClass",
        "authorization",
      ];
    }

    // Filter changeTypes based on allowed values
    return changeTypes.filter((change) => allowedTypes.includes(change.value));
  };

  const filteredChangeTypes = getFilteredChangeTypes();

  const handleChange = (e, setFieldValue) => {
    const newValue = e.target.value;
    if (hasPendingShareCapitalTransaction) {
      // Show error message when trying to select an option
      setShowError(true);
      return;
    }
    setShowError(false);
    setTypeOfChange(newValue);
    setFieldValue("typeOfChange", newValue);
  };

  return (
    <div>
      <Formik
        enableReinitialize
        initialValues={{
          shareCapitalSelection: "allotmentOfShares",
          typeOfChange: typeOfChange || "",
        }}
        validationSchema={validationSchema}
      >
        {({ values, setFieldValue, errors, touched }) => (
          <Form className="bg-white rounded flex gap-3 py-6 px-3">
            <div className="w-full flex flex-col gap-2">
              <p>Type of change</p>
              <Field
                as="select"
                name="shareCapitalSelection"
                className="w-full rounded border border-gray-200 p-2 cursor-not-allowed disabled"
                disabled
              >
                {selectionTypes.map((change, index) => (
                  <option
                    key={index}
                    value={change.value}
                    disabled={change.disabled}
                  >
                    {change.label}
                  </option>
                ))}
              </Field>
              {errors.shareCapitalSelection && touched.shareCapitalSelection ? (
                <div className="text-red-600">
                  {errors.shareCapitalSelection}
                </div>
              ) : null}
            </div>
            <div className="w-full flex flex-col gap-2">
              <p>Options</p>
              <Field
                as="select"
                name="typeOfChange"
                className="w-full rounded border border-gray-200 p-2"
                onChange={(e) => handleChange(e, setFieldValue)}
              >
                {filteredChangeTypes.map((change, index) => (
                  <option
                    key={index}
                    value={change.value}
                    disabled={change.disabled}
                  >
                    {change.label}
                  </option>
                ))}
              </Field>
              {!showError && errors.typeOfChange && touched.typeOfChange ? (
                <div className="text-red-600">{errors.typeOfChange}</div>
              ) : null}
              {showError && hasPendingShareCapitalTransaction && (
                <div className="text-red-600 mt-2">
                  There is a pending Share Capital transaction. Please complete
                  it before initiating a new request.
                </div>
              )}
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default TypeOfChange;
